import React, { useState } from "react";
import { ArrowRight, CheckCircle2, Mail, MessageSquareText, UserRoundCheck } from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import SubHero from "@/components/layout/SubHero";
import { Reveal } from "@/components/landing/Shared";

const focusOptions = [
  "Stress and nervous-system regulation",
  "Anxiety and mental urgency",
  "Sleep and recovery",
  "Relationships and communication",
  "Focus, productivity, and consistency",
  "Social confidence and connection",
  "Habits and behaviour change",
  "Performance, exercise, or recovery",
];

const fieldClass = "mt-2 block min-h-12 w-full rounded-2xl border bg-white px-4 py-3 text-sm forr-text-ink focus:outline-none focus:ring-2 focus:ring-teal-500/30";

const Coaching = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    preferredContact: "Email",
    focus: [],
    goals: "",
    availability: "",
    website: "",
  });
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const toggleFocus = (value) => setForm((current) => ({
    ...current,
    focus: current.focus.includes(value)
      ? current.focus.filter((item) => item !== value)
      : [...current.focus, value],
  }));

  const submit = async (event) => {
    event.preventDefault();
    setStatus("sending");
    setMessage("");
    try {
      const response = await fetch("/api/coaching-inquiry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, source: "adaptive-physiology-coaching" }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || "Your enquiry could not be sent. Please try again.");
      setStatus("sent");
      setMessage(body.message || "Your Adaptive Physiology coaching enquiry has been sent.");
    } catch (error) {
      setStatus("error");
      setMessage(error.message);
    }
  };

  return (
    <PageShell>
      <SubHero
        testId="coaching-hero"
        breadcrumb={[{ label: "Home", to: "/" }, { label: "1-to-1 Coaching" }]}
        overline="Adaptive Physiology · Individual support"
        headline="Understand your pattern."
        headlineAccent="Apply it to your life."
        lead="1-to-1 coaching turns the information in your Adaptive Assessment into a practical, individualized process for improving regulation, follow-through, connection, recovery, and performance."
      />

      <section className="relative py-16 sm:py-24">
        <div className="mx-auto grid max-w-[1120px] gap-8 px-6 sm:px-10 lg:grid-cols-[0.78fr_1.22fr] lg:px-16">
          <Reveal>
            <div className="space-y-5 lg:sticky lg:top-28">
              <div className="rounded-[26px] p-7 forr-panel-information">
                <UserRoundCheck size={24} className="forr-text-teal-deep" />
                <h2 className="mt-5 font-display text-3xl forr-text-ink">What coaching adds</h2>
                <p className="mt-4 text-sm leading-relaxed forr-text-soft">
                  The assessment helps you see the pattern. Coaching helps you recognize it in real situations, choose a useful response, and build repeatable experiences that your nervous system can learn from.
                </p>
                <ul className="mt-6 space-y-3 text-sm leading-relaxed forr-text-ink">
                  {[
                    "A guided review of your assessment profile",
                    "Clear priorities based on your present demands and capacity",
                    "Practical application to work, relationships, habits, movement, and recovery",
                    "Ongoing observation and adjustment as your response changes",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3"><CheckCircle2 size={17} className="mt-0.5 shrink-0 forr-text-teal-deep" />{item}</li>
                  ))}
                </ul>
              </div>
              <div className="rounded-[24px] p-6 forr-surface" style={{ border: "1px solid var(--forr-line-soft)" }}>
                <MessageSquareText size={21} className="forr-text-teal-deep" />
                <h3 className="mt-4 font-display text-2xl forr-text-ink">A conversation with context</h3>
                <p className="mt-3 text-sm leading-relaxed forr-text-soft">Tell us what you want to understand or change. Your enquiry will arrive separately from FORR Health’s regular personal-training forms and will be identified as an Adaptive Physiology coaching request.</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            <div className="rounded-[30px] p-6 forr-surface sm:p-9" style={{ border: "1px solid var(--forr-line)" }}>
              {status === "sent" ? (
                <div data-testid="coaching-form-success" className="py-12 text-center">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-teal-tint"><CheckCircle2 size={27} className="forr-text-teal-deep" /></div>
                  <h2 className="mt-6 font-display text-3xl forr-text-ink">Your enquiry is in.</h2>
                  <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed forr-text-soft">{message} Corey will review what you shared and reply using your preferred contact method.</p>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="coaching-inquiry-form">
                  <div className="flex items-start gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-teal-tint"><Mail size={21} className="forr-text-teal-deep" /></div>
                    <div>
                      <div className="forr-overline">Adaptive Physiology enquiry</div>
                      <h2 className="mt-1 font-display text-3xl forr-text-ink">Ask about 1-to-1 coaching</h2>
                    </div>
                  </div>

                  <div className="mt-8 grid gap-5 sm:grid-cols-2">
                    <label className="text-sm forr-text-soft">Name
                      <input required maxLength="100" value={form.name} onChange={(event) => update("name", event.target.value)} className={fieldClass} />
                    </label>
                    <label className="text-sm forr-text-soft">Email
                      <input required type="email" autoComplete="email" maxLength="320" value={form.email} onChange={(event) => update("email", event.target.value)} className={fieldClass} />
                    </label>
                    <label className="text-sm forr-text-soft">Phone <span className="forr-text-mute">(optional)</span>
                      <input type="tel" autoComplete="tel" maxLength="40" value={form.phone} onChange={(event) => update("phone", event.target.value)} className={fieldClass} />
                    </label>
                    <label className="text-sm forr-text-soft">Preferred contact
                      <select value={form.preferredContact} onChange={(event) => update("preferredContact", event.target.value)} className={fieldClass}>
                        <option>Email</option><option>Phone</option><option>Text message</option>
                      </select>
                    </label>
                  </div>

                  <fieldset className="mt-7">
                    <legend className="text-sm font-medium forr-text-ink">What would you like support with?</legend>
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      {focusOptions.map((option) => (
                        <label key={option} className="flex cursor-pointer items-start gap-3 rounded-2xl p-3 text-sm forr-surface-tint forr-text-soft">
                          <input type="checkbox" checked={form.focus.includes(option)} onChange={() => toggleFocus(option)} className="mt-1" />
                          <span>{option}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  <label className="mt-7 block text-sm forr-text-soft">What would you most like to understand or change?
                    <textarea required rows={6} maxLength="3000" value={form.goals} onChange={(event) => update("goals", event.target.value)} className={`${fieldClass} resize-y`} placeholder="A short description is enough. You can mention where the pattern shows up, what you have tried, and what a useful change would look like." />
                  </label>

                  <label className="mt-5 block text-sm forr-text-soft">General availability <span className="forr-text-mute">(optional)</span>
                    <input maxLength="300" value={form.availability} onChange={(event) => update("availability", event.target.value)} className={fieldClass} placeholder="For example: weekday evenings or Saturday mornings" />
                  </label>

                  <label className="hidden" aria-hidden="true">Website
                    <input tabIndex="-1" autoComplete="off" value={form.website} onChange={(event) => update("website", event.target.value)} />
                  </label>

                  {status === "error" && <p className="mt-5 rounded-2xl bg-red-50 p-4 text-sm text-red-800" role="alert">{message}</p>}

                  <button type="submit" disabled={status === "sending"} className="mt-7 inline-flex items-center gap-2 rounded-full px-7 py-4 text-sm font-semibold forr-btn-primary disabled:cursor-not-allowed disabled:opacity-50">
                    {status === "sending" ? "Sending enquiry…" : "Send coaching enquiry"}<ArrowRight size={16} />
                  </button>
                  <p className="mt-4 text-xs leading-relaxed forr-text-mute">Your information is sent directly to FORR Health for this Adaptive Physiology coaching enquiry.</p>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </PageShell>
  );
};

export default Coaching;
